import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import React from 'react';
import { Permission } from '@futrli-sdk/workspaces';
import { useErrorHandler } from '@futrli/errors';
import {
  matchSettingsPath,
  storeCurrentLocation,
  getWorkspaceDetailSettingsUrl,
  GO_BACK_PREFIX,
  ErrorTracker,
  checkNavPermission,
  ROUTES,
  joinRouteMatchUrl
} from '@futrli/shared-services';
import { signOut } from '@futrli/auth';
import { useSxpContext } from '@futrli/predict';

import { EXIT_SETTINGS_KEY } from '../../core/constants';
import { LDProps, getDefaultLaunchDarklyUser } from '../../core/providers/launch-darkly';
import { SettingsList } from './settings-list';

interface OwnProps {
  readonly workspaceId: string;
  readonly permissions: Permission[];
}

type Props = OwnProps & LDProps;

const USERS_SETTINGS_FILTER_KEY = 'users-settings-filters';
const INVOICES_BILLS_FILTER_KEY = 'invoices-bills-filters';
const PERFORMANCE_COMPARISON_FILTER_KEY = 'performance-comparison-filters';
const EXPORTED_PDFS_FILTER_KEY = 'exported-pdfs-filters';
const SCHEDULED_REPORTS_FILTER_KEY = 'scheduled-reports-filters';
const AWAITING_REVIEW_FILTER_KEY = 'awaiting-review-filters';
const SENT_PDFS_FILTER_KEY = 'sent-pdfs-filters';

export const SettingsContainer = ({ permissions, workspaceId, ldClient }: Props) => {
  const handleError = useErrorHandler();

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { onlyExternalOrgs } = useSxpContext();

  const _storeCurrentLocation = () => {
    // Let's not store the exit link if we're already in settings
    if (!matchSettingsPath(location.pathname)) {
      storeCurrentLocation(EXIT_SETTINGS_KEY);
    }
  };

  const handleOrganisationsClick = () => {
    _storeCurrentLocation();
    navigate(getWorkspaceDetailSettingsUrl(workspaceId));
  };

  const handleUsersClick = () => {
    _storeCurrentLocation();
    navigate(joinRouteMatchUrl('', getWorkspaceDetailSettingsUrl(workspaceId), ROUTES.USERS_ROUTE));
  };
  const handleLogoutClick = async (event: React.MouseEvent<HTMLElement>): Promise<void> => {
    event.preventDefault();

    try {
      await dispatch(signOut());

      localStorage.removeItem(USERS_SETTINGS_FILTER_KEY);
      localStorage.removeItem(INVOICES_BILLS_FILTER_KEY);
      localStorage.removeItem(PERFORMANCE_COMPARISON_FILTER_KEY);
      localStorage.removeItem(EXPORTED_PDFS_FILTER_KEY);
      localStorage.removeItem(SCHEDULED_REPORTS_FILTER_KEY);
      localStorage.removeItem(AWAITING_REVIEW_FILTER_KEY);
      localStorage.removeItem(SENT_PDFS_FILTER_KEY);

      // reset feature flag properties
      if (ldClient) {
        const defaultLDUser = await getDefaultLaunchDarklyUser();

        ldClient.identify(defaultLDUser);
        ldClient.flush();
      }

      localStorage.removeItem(`${GO_BACK_PREFIX}:${EXIT_SETTINGS_KEY}`);

      sessionStorage.clear();

      // clean Sentry scope
      ErrorTracker.cleanScope();
    } catch (e) {
      handleError(e);
    }
  };

  const hasUsersPermissions = checkNavPermission(permissions, ROUTES.MEMBERS_ROUTE) && !onlyExternalOrgs;

  return (
    <SettingsList
      handleLogoutClick={handleLogoutClick}
      handleOrganisationsClick={handleOrganisationsClick}
      hasUsersPermissions={hasUsersPermissions}
      handleUsersClick={handleUsersClick}
    />
  );
};
